import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Interruttore } from '@/components/common/Interruttore';
import { Loader } from '@/components/common/Loader';
import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';
import {
  chiaveErrore,
  chiaveRiga,
  listaSuperata,
  motivoBloccata,
  raggruppaPerFamiglia,
} from '@/lib/notifiche/consenso';
import { useNotificationConsentQuery, useSetNotificationConsentMutation } from '@/services/qbotApi';
import type { NotificationConsentRow } from '@/types/qbot';

/**
 * «Notifiche» (impostazioni, L-2500 — famiglia «amici»): un interruttore per ogni riga che
 * `GET /api/v1/me/notifications/consent` restituisce, raggruppato per famiglia e con il canale scritto.
 *
 * Regole (contratto letto a QBot 0160df7, `src/api/v1/me/notificationConsent.ts`):
 *  · si disegna SOLO cio' che il server manda: `amici` non c'e' finche' QBot non la accende, `system` non
 *    c'e' mai — nessun buco, nessuna testata vuota;
 *  · una riga `locked` non si preme e dice perche';
 *  · mentre la PATCH e' in volo la riga mostra lo stato di prima e non si preme; lo stato nuovo e'
 *    quello che il server RISPONDE (la riga riletta dal freno), non quello che si e' chiesto;
 *  · su errore lo stato e' quello di prima, con una frase: 409 e 400 hanno la loro, non un errore generico.
 * ⛔ Nessun token qui: lo mette `qbotApi` (`prepareHeaders`), e non finisce in nessun testo di errore.
 */
export function FriendsNotifiche() {
  const { t } = useTranslation('general');
  const consensi = useNotificationConsentQuery();
  const gruppi = consensi.data ? raggruppaPerFamiglia(consensi.data) : [];

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <Link
        to="/friends"
        data-testid="notifiche-indietro"
        className="inline-flex w-fit items-center gap-1 text-sm font-semibold text-brand"
      >
        <ChevronLeft aria-hidden className="h-4 w-4" />
        {t('social.area_friends', { defaultValue: 'Friends' })}
      </Link>

      {/* I-318, ADR-0190 §1: titolo di sezione fuori dalla vista, `sr-only`; la chiave resta in uso. */}
      <h1 className="sr-only">{t('notifiche.title', { defaultValue: 'Notifications' })}</h1>

      {consensi.data === undefined && consensi.isError ? (
        <div role="alert" className="flex flex-col items-start gap-2 rounded-2xl bg-bg-secondary p-4">
          <p className="text-sm text-fg">{t('notifiche.load_error', { defaultValue: "Couldn't load the list." })}</p>
          <button
            type="button"
            data-testid="notifiche-riprova"
            onClick={() => void consensi.refetch()}
            className="h-10 rounded-xl bg-bg-tertiary px-4 text-sm font-bold text-fg"
          >
            {t('notifiche.retry', { defaultValue: 'Retry' })}
          </button>
        </div>
      ) : consensi.data === undefined ? (
        <div className="flex h-32 items-center justify-center">
          <Loader />
        </div>
      ) : gruppi.length === 0 ? (
        <p
          data-testid="notifiche-vuoto"
          className="rounded-2xl bg-bg-secondary p-6 text-center text-sm text-fg-muted"
        >
          {t('notifiche.empty', { defaultValue: 'There are no notification settings to change yet.' })}
        </p>
      ) : (
        gruppi.map((g) => (
          <section
            key={g.family}
            data-testid={`notifiche-gruppo-${g.family}`}
            aria-label={t(`notifiche.family_${g.family}`, { defaultValue: g.family })}
            className="card-edge flex flex-col rounded-2xl bg-bg-secondary/60"
          >
            <h2 className="px-4 pb-1 pt-3 text-[11px] font-bold uppercase tracking-wide text-fg-muted">
              {t(`notifiche.family_${g.family}`, { defaultValue: g.family })}
            </h2>
            {g.righe.map((r, i) => (
              <RigaConsenso key={chiaveRiga(r)} riga={r} primo={i === 0} rileggi={() => void consensi.refetch()} />
            ))}
          </section>
        ))
      )}
    </div>
  );
}

function RigaConsenso({
  riga,
  primo,
  rileggi,
}: {
  riga: NotificationConsentRow;
  primo: boolean;
  /** Rilegge la lista dal server: dopo un 409/400 quella che si vede puo' non essere piu' la sua. */
  rileggi: () => void;
}) {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const chiave = chiaveRiga(riga);
  const [imposta, stato] = useSetNotificationConsentMutation({ fixedCacheKey: chiave });
  // L'errore e' di UN tentativo: si azzera al tocco successivo (su una riga che nel frattempo e' diventata
  // `locked` quel tocco non arriva, e la frase resta finche' si esce dalla pagina: e' voluto, dice perche').
  const [errore, setErrore] = useState<unknown>(null);

  // Su Discord «acceso» (`granted`) e «nessuna riga» (`off` + `followedOnly`) consegnano la STESSA cosa: i promemoria
  // dei tornei che segui (QBot #255). Uno switch che li mostrasse diversi racconterebbe una differenza che non c'e'.
  // `=== true` e non «truthy»: il campo e' additivo e non ancora servito, e una stringa o un numero non sono un sì.
  const acceso = riga.state === 'on' || riga.followedOnly === true;
  const inVolo = stato.isLoading;
  const spenta = riga.locked || inVolo;
  const idMotivo = `notifiche-motivo-${chiave}`;
  const idErrore = `notifiche-errore-${chiave}`;
  const idSeguiti = `notifiche-seguiti-${chiave}`;
  const perSeguiti = acceso && riga.family === 'reminder' && riga.channel === 'discord';
  // Il nome dello switch porta la famiglia: sei switch che si chiamano solo «Telegram»/«Discord» non si
  // distinguono a un lettore di schermo (revisione indipendente #512, 🟡7). Contiene il testo visibile.
  const nome = `${t(`notifiche.family_${riga.family}`, { defaultValue: riga.family })} · ${t(
    `notifiche.channel_${riga.channel}`,
    { defaultValue: riga.channel },
  )}`;
  const descritto = [riga.locked ? idMotivo : null, perSeguiti ? idSeguiti : null, errore ? idErrore : null].filter(Boolean).join(' ') || undefined;

  const premi = async () => {
    if (spenta) return;
    haptics.impact('light');
    setErrore(null);
    try {
      await imposta({ family: riga.family, channel: riga.channel, state: acceso ? 'off' : 'on' }).unwrap();
    } catch (e) {
      setErrore(e ?? new Error('errore'));
      // 409/400: la lista che si vede non e' piu' quella del server (es. la riga e' diventata `locked`).
      if (listaSuperata(e)) rileggi();
    }
  };

  return (
    <div
      data-testid={`notifiche-riga-${chiave}`}
      className={cn('flex min-h-[64px] items-center gap-3 px-4 py-2', !primo && 'border-t border-border')}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-fg">
          {t(`notifiche.channel_${riga.channel}`, { defaultValue: riga.channel })}
        </p>
        {riga.locked ? (
          <p id={idMotivo} data-testid={`notifiche-motivo-${chiave}`} className="mt-0.5 text-xs text-fg-muted">
            {t(`notifiche.${motivoBloccata(riga.family, riga.channel)}`, {
              defaultValue: "This can't be changed from here.",
            })}
          </p>
        ) : null}
        {perSeguiti ? (
          <p id={idSeguiti} data-testid={`notifiche-seguiti-${chiave}`} className="mt-0.5 text-xs text-fg-muted">
            {t('notifiche.followed_only', {
              defaultValue: 'Off: you only get reminders for the tournaments you follow.',
            })}
          </p>
        ) : null}
        {errore ? (
          <p id={idErrore} data-testid={`notifiche-errore-${chiave}`} role="status" className="mt-1 text-xs text-danger">
            {t(`notifiche.${chiaveErrore(errore)}`, { defaultValue: "Couldn't save. Try again." })}
          </p>
        ) : null}
      </div>
      <Interruttore
        acceso={acceso}
        aria-label={nome}
        aria-describedby={descritto}
        aria-busy={inVolo}
        // `aria-disabled` e non `disabled`: una riga locked resta raggiungibile col Tab e il suo motivo si
        // sente. La guardia e' `if (spenta) return` in `premi`: il click su una locked non fa niente.
        aria-disabled={spenta}
        data-testid={`notifiche-switch-${chiave}`}
        onClick={() => void premi()}
      />
    </div>
  );
}
